import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, ButtonBase, Collapse, Typography } from "@mui/material";
import { useReducedMotion } from "framer-motion";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { INK, MOTION, NEUTRAL, STATUS_BADGE, SURFACE } from "@/theme/tokens";
import MetaChip from "@/ui/Badges/MetaChip";
import type { DayStats } from "@/utils/models";
import { GapsCensusAndHppd } from "./metrics/GapsCensusAndHppd";
import { GapsMetricsDetail } from "./metrics/GapsMetricsDetail";
import { METRIC_LABEL_SX } from "./metrics/gapsMetricsStyles";
import { useGapsCountsHidden } from "./useGapsCountsHidden";
import type { GapsDayHppd } from "./useGapsDayModel";

type Props = {
	dayStats: DayStats | undefined;
	/** HPPD and target as the Day Schedule header computes them — never `dayStats.hppd`. */
	dayHppd: GapsDayHppd | undefined;
	date: DateTime;
	isExpanded: boolean;
	onToggle: () => void;
	/** True when a saved scope is narrowing the numbers, so the line says they are not the building's. */
	isNarrowed: boolean;
	/** The day shows what was punched (`GapsHomeModel.isDayActual`), so the line says its numbers are not the schedule's. */
	isActual: boolean;
};

/**
 * The day's census and HPPD, opening into the drawer of the day's stats.
 *
 * It carries no per-job breakdown: the group headers below already state each job's `filled/required`, and a
 * row of per-job marks grew past one line with many jobs or enlarged system text.
 *
 * Nothing here is computed. Census, PPD and cost come from `DayItem.dayStats` and HPPD from `useHppdByDimension` —
 * each the source the Day Schedule header prints — so the phone and the desktop cannot report different numbers
 * for one day. A day with no stats (a URL date outside the loaded week, for the moment the shell takes to redirect
 * it) shows no figures rather than a row of zeros that would read as a real, empty day.
 */
export const GapsMetricsLine: FC<Props> = ({ dayStats, dayHppd, date, isExpanded, onToggle, isNarrowed, isActual }) => {
	const { t } = useTranslation();
	const reduceMotion = useReducedMotion();
	const { areCountsHidden } = useGapsCountsHidden();

	return (
		<Box sx={{ bgcolor: SURFACE }}>
			<ButtonBase
				data-testid="gaps-metrics-line"
				onClick={onToggle}
				aria-expanded={isExpanded}
				sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1, px: 1.625, py: 1, borderTop: `1px solid ${NEUTRAL[150]}`, textAlign: "left" }}
			>
				<Box sx={{ flex: 1, minWidth: 0, display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 1.25, rowGap: 0.25 }}>
					{/* More → Hide gaps takes the counts off the strip and the groups; the note says so, so a screen without
					    them is never read as a day with nothing to count. Amber, not the muted label ink: a switched-off
					    view is a state to notice, unlike "filtered" beside live counts. The badge amber rather than
					    STATUS.WARNING.main, which is too faint for text this small. */}
					{areCountsHidden && (
						<Typography component="span" data-testid="gaps-metrics-counts-hidden-note" sx={{ ...METRIC_LABEL_SX, color: STATUS_BADGE.AMBER_INK }}>
							{t("gaps.metrics.countsHidden")}
						</Typography>
					)}
					{isActual && <MetaChip label={t("gaps.metrics.punched")} tone="success" size="small" dataTestId="gaps-metrics-punched-note" />}
					{isNarrowed && (
						<Typography component="span" data-testid="gaps-metrics-filtered-note" sx={METRIC_LABEL_SX}>
							{t("gaps.metrics.filtered")}
						</Typography>
					)}
				</Box>
				{dayStats && dayHppd && <GapsCensusAndHppd dayStats={dayStats} dayHppd={dayHppd} />}
				<ExpandMoreIcon
					sx={{
						fontSize: 16,
						color: INK.MUTED,
						flex: "none",
						transform: isExpanded ? "rotate(180deg)" : "none",
						transition: reduceMotion ? "none" : `transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
					}}
				/>
			</ButtonBase>

			{/* Opens with a height reveal and closes a touch faster — the house asymmetry: the user
			    deliberates over showing something, the system should be prompt taking it back. */}
			<Collapse
				in={isExpanded}
				timeout={reduceMotion ? 0 : { enter: MOTION.DURATION.ENTRANCE_MD, exit: MOTION.DURATION.INLINE_EXIT }}
				easing={MOTION.EASE_OUT}
				unmountOnExit
			>
				<GapsMetricsDetail dayStats={dayStats} dayHppd={dayHppd} date={date} />
			</Collapse>
		</Box>
	);
};
